import * as React from "react";
import {
  Box,
  Spinner,
  Flex,
  AvatarProps,
  useMultiStyleConfig,
  BoxProps,
} from "@chakra-ui/react";
import { Avatar } from "shared";
import { useFileUrl } from "hooks";
import { useUserProfilePicture } from "api/queries/useUserProfilePicture";
import { useAuth0 } from "@auth0/auth0-react";

type UserAvatarProps = {
  picture?: string;
  currentUserPicture?: boolean;
  boxProps?: BoxProps;
} & AvatarProps;

function UserAvatar(props: UserAvatarProps) {
  const { user } = useAuth0();
  const { currentUserPicture = false, boxProps, ...avatarProps } = props;
  const {
    name,
    title,
    picture = "",
    w,
    h,
    width,
    height,
    size,
    ...rest
  } = avatarProps;

  const nameFromProps = name ?? title;

  // Queries
  const {
    data: file,
    isLoading: isFileLoading,
    isError,
  } = useUserProfilePicture(picture, currentUserPicture);

  const fileUrl = useFileUrl(file?.data);

  const auth0FirstName = user?.given_name ?? "";
  const auth0LastName = user?.family_name ?? "";
  const auth0Name = `${auth0FirstName} ${auth0LastName}`.trim();

  const displayName = nameFromProps ?? auth0Name;

  const styles = useMultiStyleConfig("Avatar", { size });
  const widthTemplate =
    w || width || (styles.container.width as string) || "140px";
  const heightTemplate =
    h || height || (styles.container.height as string) || "140px";

  if (!displayName && !picture) {
    return null;
  }

  return (
    <Box {...boxProps}>
      {isFileLoading ? (
        <Flex
          width={widthTemplate}
          height={heightTemplate}
          align="center"
          justify="center"
          {...rest}
        >
          <Spinner color="blue" width="40px" height="40px" />
        </Flex>
      ) : isError ? (
        <Avatar bg="#9e2700" loading="eager" name={displayName} {...rest} />
      ) : (
        <Avatar
          bg="#9e2700"
          loading="eager"
          src={fileUrl || ""}
          name={displayName}
          {...rest}
        />
      )}
    </Box>
  );
}

export { UserAvatar };
